import React, { useMemo, useState } from "react";
import {
    ChevronDown,
    ChevronRight,
    Folder,
    FolderOpen,
    Search,
    X,
} from "lucide-react";
import {
    flattenTree,
    getUserPermissionForRepertoire,
    normalizeText,
    statusLabel,
} from "./kmsUi";
import "./KmsShared.css";

export function KmsPageHeader({ title, subtitle, actions }) {
    return (
        <div className="kms-header">
            <div>
                <h1>{title}</h1>
                {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {actions ? <div className="kms-header-actions">{actions}</div> : null}
        </div>
    );
}

export function KmsStatusBadge({ status }) {
    const value = status || "DRAFT";
    return <span className={`kms-badge kms-badge--${String(value).toLowerCase()}`}>{statusLabel(value)}</span>;
}

export function KmsTypeBadge({ children }) {
    return <span className="kms-type-badge">{children || "-"}</span>;
}

export function KmsEmpty({ children = "Aucune donnée disponible" }) {
    return <div className="kms-empty">{children}</div>;
}

function TreeNode({ node, level, selectedId, onSelect }) {
    const [open, setOpen] = useState(level < 1);
    const hasChildren = node.children.length > 0;

    return (
        <>
            <button
                type="button"
                className={`kms-tree-row ${String(selectedId) === String(node.id) ? "is-selected" : ""}`}
                style={{ paddingLeft: 12 + level * 20 }}
                onClick={() => onSelect(node)}
            >
                {hasChildren ? (
                    <span
                        className="kms-tree-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setOpen((value) => !value);
                        }}
                    >
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                ) : (
                    <span className="kms-tree-toggle" />
                )}
                {open ? <FolderOpen size={15} /> : <Folder size={15} />}
                <span className="kms-tree-label">{node.name || node.nom || node.code || `Répertoire ${node.id}`}</span>
            </button>
            {hasChildren && open
                ? node.children.map((child) => (
                      <TreeNode
                          key={child.id}
                          node={child}
                          level={level + 1}
                          selectedId={selectedId}
                          onSelect={onSelect}
                      />
                  ))
                : null}
        </>
    );
}

export function KmsDirectoryPicker({ open, tree, selectedId, onClose, onSelect }) {
    const [query, setQuery] = useState("");
    const flat = useMemo(() => flattenTree(tree || []), [tree]);
    const filtered = useMemo(() => {
        const value = normalizeText(query);
        if (!value) return tree || [];
        const matchingIds = new Set(
            flat
                .filter((item) =>
                    normalizeText(`${item.name || ""} ${item.code || ""} ${item.path || ""}`).includes(value)
                )
                .map((item) => item.id)
        );
        const filterNodes = (nodes = []) =>
            nodes
                .map((node) => {
                    const children = filterNodes(node.children || []);
                    if (matchingIds.has(node.id) || children.length) return { ...node, children };
                    return null;
                })
                .filter(Boolean);
        return filterNodes(tree || []);
    }, [flat, query, tree]);

    if (!open) return null;

    return (
        <div className="kms-modal-overlay" onMouseDown={onClose}>
            <div className="kms-modal kms-modal--picker" onMouseDown={(event) => event.stopPropagation()}>
                <div className="kms-modal-header">
                    <h3>Choisir un répertoire</h3>
                    <button className="kms-icon-btn" type="button" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="kms-modal-body">
                    <div className="kms-search-box">
                        <Search size={14} />
                        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Répertoire..." />
                    </div>
                    <div className="kms-tree">
                        {filtered.length ? (
                            filtered.map((node) => (
                                <TreeNode
                                    key={node.id}
                                    node={node}
                                    level={0}
                                    selectedId={selectedId}
                                    onSelect={(item) => {
                                        onSelect(item);
                                        onClose();
                                    }}
                                />
                            ))
                        ) : (
                            <KmsEmpty>Aucun répertoire trouvé</KmsEmpty>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export function KmsDirectoryWorkspace({
    selected,
    tree,
    currentUser,
    includeChildren,
    onIncludeChildrenChange,
    onSelect,
}) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const permissions = getUserPermissionForRepertoire(selected, currentUser);

    return (
        <div className="kms-workspace">
            <div className="kms-workspace-main">
                <span className="kms-workspace-label">Répertoire de travail</span>
                <strong>{selected.path || selected.__path || selected.name || selected.nom || "Aucun répertoire sélectionné"}</strong>
                <div className="kms-rights">
                    {permissions.canConsult ? <span>Consultation</span> : null}
                    {permissions.canCreate ? <span>Création</span> : null}
                    {permissions.canValidate ? <span>Validation</span> : null}
                </div>
            </div>
            <label className="kms-check">
                <input
                    type="checkbox"
                    checked={Boolean(includeChildren)}
                    onChange={(event) => onIncludeChildrenChange(event.target.checked)}
                />
                Inclure les sous-répertoires
            </label>
            <button type="button" className="kms-btn kms-btn--ghost" onClick={() => setPickerOpen(true)}>
                Choisir
            </button>
            <KmsDirectoryPicker
                open={pickerOpen}
                tree={tree}
                selectedId={selected.id}
                onClose={() => setPickerOpen(false)}
                onSelect={onSelect}
            />
        </div>
    );
}

export function KmsModal({ open, title, children, footer, onClose }) {
    if (!open) return null;
    return (
        <div className="kms-modal-overlay" onMouseDown={onClose}>
            <div className="kms-modal" onMouseDown={(event) => event.stopPropagation()}>
                <div className="kms-modal-header">
                    <h3>{title}</h3>
                    <button className="kms-icon-btn" type="button" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="kms-modal-body">{children}</div>
                {footer ? <div className="kms-modal-footer">{footer}</div> : null}
            </div>
        </div>
    );
}


